import React, { useState, useEffect } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { Helmet } from "react-helmet";
import axios from "axios";
import styles from "./Gallery.module.css";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, type: "spring" } },
};

const Gallery = () => {
  const { scrollYProgress } = useScroll();
  const parallaxY = useTransform(scrollYProgress, [0, 1], [0, -150]);
  const springParallax = useSpring(parallaxY, { stiffness: 120, damping: 25 });

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [visiblePosts, setVisiblePosts] = useState(12); // Initial number of posts to display

  const fetchInstagramFeed = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get("/wp-json/custom/v1/instagram-feed");
      const data = response.data;
      if (Array.isArray(data.posts) && data.posts.length > 0) {
        setPosts(data.posts);
      } else {
        setError("No Instagram posts found.");
      }
    } catch (err) {
      console.error("Error fetching Instagram posts:", err.message);
      setError("Failed to load gallery. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInstagramFeed();
  }, []);

  const handleLoadMore = () => {
    setVisiblePosts((prev) => prev + 12);
  };

  const handleRetry = () => {
    fetchInstagramFeed();
  };

  return (
    <>
      <Helmet>
        <title>Gallery | Studio Maamangala Wedding Photography, Baripada</title>
        <meta
          name="description"
          content="Explore Studio Maamangala’s cinematic wedding photography and videography gallery from Baripada, Odisha, capturing love and tradition."
        />
        <meta
          name="keywords"
          content="Studio Maamangala gallery, wedding photography Baripada, Odisha wedding gallery, cinematic videography, Instagram portfolio"
        />
        <meta property="og:title" content="Gallery | Studio Maamangala" />
        <meta
          property="og:description"
          content="Discover our breathtaking collection of wedding moments in Baripada, Odisha."
        />
        <meta property="og:url" content="https://studiomaamangala.com/gallery" />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="https://studiomaamangala.com/wp-content/uploads/2025/02/michael-soledad-jiOByhCw2jE-unsplash-scaled.jpg"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Gallery | Studio Maamangala, Baripada" />
        <meta name="twitter:description" content="View our cinematic wedding photography and videography portfolio." />
        <meta
          name="twitter:image"
          content="https://studiomaamangala.com/wp-content/uploads/2025/02/michael-soledad-jiOByhCw2jE-unsplash-scaled.jpg"
        />
      </Helmet>

      <motion.div className={styles.galleryPage} initial="hidden" animate="visible" variants={containerVariants}>
        {/* Hero Section */}
        <section className={styles.hero}>
          <motion.div
            className={styles.heroOverlay}
            style={{ y: springParallax }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.85 }}
            transition={{ duration: 2 }}
          />
          <motion.img
            src="https://studiomaamangala.com/wp-content/uploads/2025/02/michael-soledad-jiOByhCw2jE-unsplash-scaled.jpg"
            alt="Studio Maamangala Wedding Scene"
            className={styles.heroImage}
            style={{ y: springParallax }}
            initial={{ scale: 1.3 }}
            animate={{ scale: 1 }}
            transition={{ duration: 2.5, type: "spring" }}
          />
          <motion.div className={styles.heroContent} variants={containerVariants}>
            <motion.h1 variants={itemVariants}>Our Gallery</motion.h1>
            <motion.p variants={itemVariants}>
              Experience the artistry of Studio Maamangala through our cinematic wedding moments captured in Baripada, Odisha.
            </motion.p>
            <motion.a
              href="#gallery"
              className={styles.ctaButton}
              variants={itemVariants}
              whileHover={{ scale: 1.15, boxShadow: "0 0 20px rgba(212, 160, 23, 0.6)" }}
              whileTap={{ scale: 0.85 }}
            >
              Explore Gallery
            </motion.a>
          </motion.div>
        </section>

        {/* Gallery Section */}
        <motion.section
          className={styles.gallerySection}
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          id="gallery"
        >
          <motion.div className={styles.galleryContent}>
            <motion.h2 variants={itemVariants}>Captured Moments</motion.h2>
            {loading && (
              <motion.div
                className={styles.loading}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                <div className={styles.spinner}></div>
                <p>Loading gallery...</p>
              </motion.div>
            )}
            {error && (
              <motion.div
                className={styles.error}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                <p>{error}</p>
                <button className={styles.retryButton} onClick={handleRetry}>
                  Try Again
                </button>
              </motion.div>
            )}
            {!loading && !error && posts.length === 0 && (
              <motion.p
                className={styles.empty}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                No posts available at the moment.
              </motion.p>
            )}
            {!loading && !error && posts.length > 0 && (
              <div className={styles.galleryGrid}>
                {posts.slice(0, visiblePosts).map((post, index) => (
                  <motion.a
                    key={post.id}
                    href={post.permalink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${styles.galleryCard} ${index % 3 === 0 ? styles.tall : ""}`}
                    variants={itemVariants}
                    whileHover={{
                      scale: 1.05,
                      rotateX: 5,
                      rotateY: 5,
                      boxShadow: "0 15px 30px rgba(212, 160, 23, 0.4)",
                    }}
                    transition={{ type: "spring", stiffness: 200 }}
                    aria-label={`View Instagram post: ${post.caption || "Wedding moment"}`}
                  >
                    <img
                      src={post.media_url}
                      alt={post.caption ? post.caption.substring(0, 50) : "Wedding moment by Studio Maamangala"}
                      className={styles.galleryImage}
                      loading="lazy"
                      onError={(e) => (e.target.src = "/path/to/fallback-image.jpg")} // Fallback image
                    />
                    {post.caption && <p className={styles.caption}>{post.caption.substring(0, 80)}...</p>}
                  </motion.a>
                ))}
              </div>
            )}
            {!loading && !error && visiblePosts < posts.length && (
              <motion.div
                className={styles.loadMore}
                variants={itemVariants}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <button className={styles.loadMoreButton} onClick={handleLoadMore}>
                  Load More
                </button>
              </motion.div>
            )}
          </motion.div>
        </motion.section>

        {/* CTA Section */}
        <motion.section
          className={styles.ctaSection}
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <motion.div className={styles.ctaContent}>
            <motion.h2 variants={itemVariants}>Join Our Journey</motion.h2>
            <motion.p variants={itemVariants}>
              Follow us on Instagram for more breathtaking wedding stories and behind-the-scenes moments.
            </motion.p>
            <motion.a
              href="https://www.instagram.com/maa_mangala_studio/"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.ctaButton}
              variants={itemVariants}
              whileHover={{ scale: 1.15, boxShadow: "0 0 20px rgba(212, 160, 23, 0.6)" }}
              whileTap={{ scale: 0.85 }}
            >
              Follow Us
            </motion.a>
          </motion.div>
        </motion.section>
      </motion.div>
    </>
  );
};

export default Gallery;